import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, Star, Briefcase, Clock, TrendingUp, Zap, Award } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from "recharts";

const MEDALS = ["", "bg-amber-400", "bg-zinc-300", "bg-amber-700"];

export default function Leaderboard() {
  const [techs, setTechs] = useState([]);
  const [period, setPeriod] = useState("30");
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("score");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const days = parseInt(period);
      const from = new Date(Date.now() - days * 86400000).toISOString();
      const to = new Date().toISOString();
      const params = `?date_from=${from}&date_to=${to}`;

      const [pnlRes, ratingsRes] = await Promise.all([
        API.get(`/financial/pnl/by-technician${params}`),
        API.get("/ratings/summary/all"),
      ]);

      const pnlMap = {};
      (pnlRes.data.technicians || []).forEach(t => { pnlMap[t.vendor_id] = t; });

      const ratingMap = {};
      (ratingsRes.data.technicians || []).forEach(t => { ratingMap[t.vendor_id] = t; });

      const allIds = new Set([...Object.keys(pnlMap), ...Object.keys(ratingMap)]);
      const combined = Array.from(allIds).map(id => {
        const pnl = pnlMap[id] || {};
        const rat = ratingMap[id] || {};
        const rating = rat.average_rating || 0;
        const jobs = pnl.jobs_count || 0;
        const revenue = pnl.revenue || 0;
        const profit = pnl.profit || 0;
        // Composite score: weighted combination
        const score = Math.round(rating * 20 + jobs * 5 + (profit > 0 ? Math.min(profit / 100, 50) : 0));
        return {
          id,
          name: pnl.name || rat.name || "Unknown",
          rating,
          totalRatings: rat.total_ratings || 0,
          jobs,
          revenue,
          profit,
          mileage: pnl.mileage || 0,
          expenses: pnl.expenses || 0,
          score,
        };
      }).filter(t => t.name && t.name !== "Unknown");

      combined.sort((a, b) => b.score - a.score);
      setTechs(combined);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sorted = [...techs].sort((a, b) => {
    if (sortBy === "score") return b.score - a.score;
    if (sortBy === "rating") return b.rating - a.rating;
    if (sortBy === "jobs") return b.jobs - a.jobs;
    if (sortBy === "revenue") return b.revenue - a.revenue;
    return 0;
  });

  const chartData = sorted.slice(0, 8).map(t => ({
    name: t.name.split(" ")[0],
    rating: t.rating,
    jobs: t.jobs,
    revenue: Math.round(t.revenue),
  }));

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="leaderboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-zinc-900 flex items-center gap-2">
            <Trophy className="w-6 h-6 text-amber-500" /> Technician Leaderboard
          </h1>
          <p className="text-sm text-zinc-500">Performance rankings and achievements</p>
        </div>
        <div className="flex gap-2">
          <select value={period} onChange={e => setPeriod(e.target.value)}
            className="px-3 py-1.5 border border-zinc-300 rounded-lg text-xs bg-white"
            data-testid="leaderboard-period">
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            className="px-3 py-1.5 border border-zinc-300 rounded-lg text-xs bg-white"
            data-testid="leaderboard-sort">
            <option value="score">Overall Score</option>
            <option value="rating">Rating</option>
            <option value="jobs">Jobs Completed</option>
            <option value="revenue">Revenue</option>
          </select>
        </div>
      </div>

      {/* Top 3 Podium */}
      {sorted.length >= 3 && (
        <div className="grid grid-cols-3 gap-3" data-testid="podium">
          {[1, 0, 2].map(idx => {
            const t = sorted[idx];
            if (!t) return null;
            const rank = idx + 1;
            const isFirst = rank === 1;
            return (
              <Card key={t.id} className={`border ${isFirst ? "border-amber-300 bg-amber-50/30 row-span-2" : "border-zinc-200"} ${rank === 1 ? "order-2" : rank === 2 ? "order-1" : "order-3"}`}
                data-testid={`podium-${rank}`}>
                <CardContent className={`${isFirst ? "p-5" : "p-4"} text-center`}>
                  <div className={`w-8 h-8 rounded-full ${MEDALS[rank] || "bg-zinc-200"} flex items-center justify-center mx-auto mb-2`}>
                    <span className="text-xs font-bold text-white">{rank}</span>
                  </div>
                  <p className={`font-heading font-bold ${isFirst ? "text-base" : "text-sm"} text-zinc-900`}>{t.name}</p>
                  <div className="flex items-center justify-center gap-1 mt-1">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star key={s} className={`${isFirst ? "w-4 h-4" : "w-3 h-3"} ${s <= Math.round(t.rating) ? "fill-amber-400 text-amber-400" : "text-zinc-200"}`} />
                    ))}
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">{t.rating}/5 ({t.totalRatings})</p>
                  <div className="grid grid-cols-2 gap-1 mt-2">
                    <div className="bg-zinc-50 rounded p-1.5">
                      <p className="text-[10px] text-zinc-400">Jobs</p>
                      <p className="text-xs font-bold">{t.jobs}</p>
                    </div>
                    <div className="bg-zinc-50 rounded p-1.5">
                      <p className="text-[10px] text-zinc-400">Score</p>
                      <p className="text-xs font-bold text-amber-600">{t.score}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Chart */}
      {chartData.length > 0 && (
        <Card className="border border-zinc-200">
          <CardContent className="p-5">
            <h3 className="text-sm font-heading font-semibold text-zinc-900 mb-4">Performance Comparison</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="jobs" fill="#3b82f6" name="Jobs" />
                <Bar dataKey="rating" fill="#f59e0b" name="Rating" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Full Rankings Table */}
      <Card className="border border-zinc-200">
        <CardContent className="p-4">
          <h3 className="text-sm font-heading font-semibold text-zinc-900 mb-3">Full Rankings</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="rankings-table">
              <thead>
                <tr className="border-b border-zinc-200 text-left">
                  <th className="py-2 px-2 text-[10px] text-zinc-500 uppercase font-bold w-8">#</th>
                  <th className="py-2 px-2 text-[10px] text-zinc-500 uppercase font-bold">Technician</th>
                  <th className="py-2 px-2 text-[10px] text-zinc-500 uppercase font-bold text-center">Score</th>
                  <th className="py-2 px-2 text-[10px] text-zinc-500 uppercase font-bold text-center">Rating</th>
                  <th className="py-2 px-2 text-[10px] text-zinc-500 uppercase font-bold text-right">Jobs</th>
                  <th className="py-2 px-2 text-[10px] text-zinc-500 uppercase font-bold text-right">Revenue</th>
                  <th className="py-2 px-2 text-[10px] text-zinc-500 uppercase font-bold text-right">Profit</th>
                  <th className="py-2 px-2 text-[10px] text-zinc-500 uppercase font-bold text-right">Mileage</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((t, i) => (
                  <tr key={t.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`rank-${i + 1}`}>
                    <td className="py-2.5 px-2">
                      {i < 3 ? (
                        <div className={`w-5 h-5 rounded-full ${MEDALS[i + 1]} flex items-center justify-center`}>
                          <span className="text-[9px] font-bold text-white">{i + 1}</span>
                        </div>
                      ) : (
                        <span className="text-zinc-400 font-mono text-xs">{i + 1}</span>
                      )}
                    </td>
                    <td className="py-2.5 px-2 font-medium text-zinc-800">{t.name}</td>
                    <td className="py-2.5 px-2 text-center">
                      <Badge className="bg-amber-100 text-amber-700 text-[10px] font-bold">{t.score}</Badge>
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      <span className="flex items-center justify-center gap-0.5">
                        <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                        <span className="font-bold">{t.rating}</span>
                      </span>
                    </td>
                    <td className="py-2.5 px-2 text-right text-zinc-600">{t.jobs}</td>
                    <td className="py-2.5 px-2 text-right text-emerald-600 font-medium">${t.revenue.toLocaleString()}</td>
                    <td className={`py-2.5 px-2 text-right font-medium ${t.profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                      ${t.profit.toLocaleString()}
                    </td>
                    <td className="py-2.5 px-2 text-right text-zinc-500">{t.mileage} mi</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
